import { createBrowserRouter, redirect } from 'react-router';

import { HomePage } from '@/features/home/HomePage';
import { ConfirmPage } from '@/features/auth/ConfirmPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { MfaSetupPage } from '@/features/auth/MfaSetupPage';
import { SignInPage } from '@/features/auth/SignInPage';
import { SignUpPage } from '@/features/auth/SignUpPage';
import { AppShell } from '@/components/layout/AppShell';
import { OrdersPage } from '@/features/orders/OrdersPage';
import { OrderDetailPage } from '@/features/orders/OrderDetailPage';
import { StoresPage } from '@/features/stores/StoresPage';
import { CarriersPage } from '@/features/carriers/CarriersPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { Step1CompanyInfo } from '@/features/onboarding/Step1CompanyInfo';
import { Step2ShipFrom } from '@/features/onboarding/Step2ShipFrom';
import { Step3ConnectStore } from '@/features/onboarding/Step3ConnectStore';
import { Step4Carrier } from '@/features/onboarding/Step4Carrier';
import { Step5Billing } from '@/features/onboarding/Step5Billing';
import { BcClaimPage, bcClaimLoader } from '@/features/bigcommerce/BcClaimPage';
import { NotFoundPage } from '@/features/errors/NotFoundPage';
import { requireAuth, requireInOnboarding, requireOnboarded } from '@/lib/auth/auth-guard';

/**
 * Route tree per apps/web/CLAUDE.md §4.
 *
 * Public routes: /, /signin, /signup, /confirm, /forgot.
 * `/auth/mfa-setup` — auth-required (`requireAuth` only).
 * `/app` — `requireOnboarded`: redirects into the wizard if the user's
 *   tenant has no `onboarded_at`.
 * `/app/onboarding/step/{1..5}` — `requireInOnboarding`: redirects to
 *   `/app` if the tenant is already onboarded (prevents re-entering
 *   the wizard via the URL bar after finishing).
 */
export const router = createBrowserRouter([
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/signin',
    element: <SignInPage />,
  },
  {
    path: '/signup',
    element: <SignUpPage />,
  },
  {
    path: '/confirm',
    element: <ConfirmPage />,
  },
  {
    path: '/forgot',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/auth/mfa-setup',
    loader: requireAuth,
    element: <MfaSetupPage />,
  },
  {
    // Dashboard shell — `requireOnboarded` gates all children; the
    // AppShell renders the sidebar + topbar + an <Outlet> for the
    // section pages. `/app` itself redirects to the orders list (the
    // default post-onboarding landing per apps/web/CLAUDE.md §4).
    path: '/app',
    loader: requireOnboarded,
    element: <AppShell />,
    children: [
      { index: true, loader: () => redirect('/app/orders') },
      { path: 'orders', element: <OrdersPage /> },
      { path: 'orders/:orderId', element: <OrderDetailPage /> },
      { path: 'stores', element: <StoresPage /> },
      { path: 'carriers', element: <CarriersPage /> },
      { path: 'settings', element: <SettingsPage /> },
    ],
  },
  {
    // Terminal step of the BC-initiated install flow (ADR 0011).
    // Reached from /signup?bc_claim=… (post-signup) or
    // /signin?next=/app/bc-claim?id=… (returning user). The loader
    // attaches the connection and bounces to /app on the happy path.
    path: '/app/bc-claim',
    loader: bcClaimLoader,
    element: <BcClaimPage />,
  },
  {
    path: '/app/onboarding',
    loader: () => redirect('/app/onboarding/step/1'),
  },
  {
    path: '/app/onboarding/step/1',
    loader: requireInOnboarding,
    element: <Step1CompanyInfo />,
  },
  {
    path: '/app/onboarding/step/2',
    loader: requireInOnboarding,
    element: <Step2ShipFrom />,
  },
  {
    path: '/app/onboarding/step/3',
    loader: requireInOnboarding,
    element: <Step3ConnectStore />,
  },
  {
    path: '/app/onboarding/step/4',
    loader: requireInOnboarding,
    element: <Step4Carrier />,
  },
  {
    path: '/app/onboarding/step/5',
    loader: requireInOnboarding,
    element: <Step5Billing />,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
]);
